/* EditorialList — the system primitive that replaces every
   `bg-card border border-border` panel across the 8 sibling admin pages.

   Composition pattern: a dechromed container with gold hairlines at the top
   and bottom of the list, every row separated by a 1px gold rule, hover
   tints rows with `rgba(201, 169, 97, 0.04)`. Rows are flex-aligned so the
   gold #0001 numeric prefix sits left, serif italic title is the primary
   readable, Manrope meta sits muted to the right, and an action slot pins
   to the far right.

   Exports: EditorialList, EditorialRow, EditorialStatusPill, EditorialAction. */

import React from "react";

export function EditorialList({ children, dense = false, className = "", ...rest }) {
  return (
    <div
      className={`border-y border-[color:var(--gold-foil)]/35 ${dense ? "" : "py-1"} ${className}`}
      {...rest}
    >
      {children}
    </div>
  );
}

/* EditorialRow — single horizontal flow row. Optional `as` overrides the
   wrapper element (button-like when `onClick`/`href`). */
export function EditorialRow({
  index,
  prefix,
  title,
  meta,
  status,
  actions,
  onClick,
  href,
  className = "",
  children,
  ...rest
}) {
  const computedPrefix =
    prefix !== undefined
      ? prefix
      : (typeof index === "number" ? `#${String(index + 1).padStart(4, "0")}` : null);
  const metaParts = Array.isArray(meta) ? meta.filter(Boolean) : meta ? [meta] : [];
  const interactive = !!(onClick || href);

  const inner = (
    <>
      {computedPrefix && (
        <span
          className="font-serif italic text-[color:var(--gold-base)] tabular-nums text-xs sm:text-sm shrink-0 w-14"
          aria-hidden
        >
          {computedPrefix}
        </span>
      )}
      <div className="flex-1 min-w-0 flex items-baseline gap-4 flex-wrap">
        {title && (
          <span className="font-serif text-base sm:text-lg text-foreground truncate">
            {title}
          </span>
        )}
        {metaParts.length > 0 && (
          <span className="text-[11px] text-muted-foreground/70 truncate flex items-center gap-2 flex-wrap">
            {metaParts.map((m, i) => (
              <React.Fragment key={i}>
                {i > 0 && <span className="opacity-40" aria-hidden>—</span>}
                <span>{m}</span>
              </React.Fragment>
            ))}
          </span>
        )}
        {children}
      </div>
      {status}
      {actions && (
        <div className="flex items-center gap-3 shrink-0">{actions}</div>
      )}
    </>
  );

  const base = `flex items-center flex-wrap gap-x-4 gap-y-2 py-4 px-2 border-b border-[color:var(--gold-foil)]/15 last:border-b-0 transition-colors ${interactive ? "hover:bg-[color:var(--gold-foil)]/[0.04] cursor-pointer text-left w-full" : ""} ${className}`;

  if (href) {
    return <a href={href} className={base} {...rest}>{inner}</a>;
  }
  if (onClick) {
    return <button type="button" onClick={onClick} className={base} {...rest}>{inner}</button>;
  }
  return <div className={base} {...rest}>{inner}</div>;
}

/* EditorialStatusPill — hairline-bordered pill in tonal accent colors. */
const TONE = {
  gold:   { border: "var(--gold-base)",       color: "var(--gold-base)" },
  cream:  { border: "rgba(245,242,236,0.5)",  color: "rgba(245,242,236,0.8)" },
  sage:   { border: "#9CAF88",                color: "#9CAF88" },
  mauve:  { border: "#A78BAA",                color: "#A78BAA" },
  red:    { border: "#C77272",                color: "#C77272" },
};

export function EditorialStatusPill({ tone = "cream", children, className = "" }) {
  const t = TONE[tone] || TONE.cream;
  return (
    <span
      className={`inline-flex items-center px-2.5 h-6 text-[9.5px] uppercase tracking-[0.28em] border ${className}`}
      style={{ borderColor: t.border, color: t.color, background: "transparent" }}
    >
      {children}
    </span>
  );
}

/* EditorialAction — uppercase tracking-widest action link with arrow glyph. */
export function EditorialAction({ tone = "gold", icon, children, onClick, href, ...rest }) {
  const color =
    tone === "red" ? "#C77272" :
    tone === "cream" ? "rgba(245,242,236,0.75)" :
    "var(--gold-base)";
  const cls = "inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.28em] hover:opacity-70 transition-opacity";
  const inner = <>{icon}{children}<span aria-hidden>→</span></>;
  if (href) return <a href={href} className={cls} style={{ color }} {...rest}>{inner}</a>;
  return <button type="button" onClick={onClick} className={cls} style={{ color }} {...rest}>{inner}</button>;
}

EditorialList.Empty = function EditorialListEmpty({ children }) {
  return <div className="py-12">{children}</div>;
};

export default EditorialList;
